import { playerlistAtom, playerlistNameModeAtom, serverMutexAtom, type PlayerlistNameMode } from "@/hooks/playerlist";
import { useAtom } from "jotai";
import cleanPlayerName from "@shared/cleanPlayerName";
import { useAtomValue } from "jotai";
import { VirtualItem, useVirtualizer } from '@tanstack/react-virtual';
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { PlayerlistPlayerType } from "@shared/socketioTypes";
import { PlayerlistPlaytimesResp } from "@shared/playerApiTypes";
import { Input } from "@/components/ui/input";
import { FilterXIcon, SlidersHorizontalIcon, XIcon } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { useOpenPlayerModal } from "@/hooks/playerModal";
import { useBackendApi } from "@/hooks/fetch";
import InlineCode from "@/components/InlineCode";
import { useEventListener } from "usehooks-ts";
import { HoverTip } from '@/components/ui/tooltip';
import { msToShortDuration } from "@/lib/dateTime";
import TeamStar from "@/components/TeamStar";

const PLAYTIME_REFRESH_INTERVAL = 30_000;
const PLAYTIME_CHANGE_DEBOUNCE = 1_500;
const PLAYTIME_PRESETS = [1, 5, 10, 25, 50];
const ROW_HEIGHT = 30;
const ROW_HEIGHT_GAME_DATA = 60;

type PlayerlistSortOrder = 'joinOrder' | 'recentlyJoined';
type PlaytimeMap = Record<number, number>;
const EMPTY_PLAYTIMES: PlaytimeMap = {};


//NOTE: Move the styles (except color) to global.css since this component is rendered often
function TagColor({ color }: { color: string }) {
    return <div
        className="outline-none focus:outline-none shrink-0"
        style={{
            display: 'inline-block',
            backgroundColor: color,
            width: '0.375rem',
            borderRadius: '2px',
        }}
    >&nbsp;</div>;
}


const nameModes = [
    { value: 'fivem', label: 'FiveM-Name', color: 'hsl(var(--fg))' },
    { value: 'rp', label: 'RP-Name', color: 'hsl(var(--info))' },
] as const;

//brand color, the same in both themes
const creatorColor = '#9146FF';


type PlayerlistFilterProps = {
    filterString: string;
    setFilterString: (s: string) => void;
    nameMode: PlayerlistNameMode;
    setNameMode: (m: PlayerlistNameMode) => void;
    teamOnly: boolean;
    setTeamOnly: (v: boolean) => void;
    creatorOnly: boolean;
    setCreatorOnly: (v: boolean) => void;
    maxPlayHours: number | null;
    setMaxPlayHours: (h: number | null) => void;
    sortOrder: PlayerlistSortOrder;
    setSortOrder: (s: PlayerlistSortOrder) => void;
};
function PlayerlistFilter({
    filterString, setFilterString,
    nameMode, setNameMode,
    teamOnly, setTeamOnly,
    creatorOnly, setCreatorOnly,
    maxPlayHours, setMaxPlayHours,
    sortOrder, setSortOrder,
}: PlayerlistFilterProps) {
    const isFiltering = nameMode !== 'fivem'
        || teamOnly
        || creatorOnly
        || maxPlayHours !== null
        || sortOrder !== 'joinOrder';
    const inputRef = useRef<HTMLInputElement>(null);
    useEventListener('message', (e: TxMessageEvent) => {
        if (e.data.type === 'globalHotkey' && e.data.action === 'focusPlayerlistFilter') {
            inputRef.current?.focus();
        }
    });

    const resetAll = () => {
        setNameMode('fivem');
        setTeamOnly(false);
        setCreatorOnly(false);
        setMaxPlayHours(null);
        setSortOrder('joinOrder');
        setFilterString('');
    };

    return (
        <div className="pt-2 px-2 flex gap-2">
            <div className="relative w-full">
                <Input
                    ref={inputRef}
                    className="h-8 lg:h-8"
                    placeholder="Name, Charakter, Job oder ID"
                    value={filterString}
                    onChange={(e) => setFilterString(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            setFilterString('');
                        }
                    }}
                />
                {filterString ? (
                    <HoverTip label="Filter löschen">
                        <button
                            type="button"
                            aria-label="Filter löschen"
                            className={cn(
                                'absolute right-2 top-1/2 -translate-y-1/2',
                                'grid place-items-center size-5',
                                'text-faint transition-colors hover:text-accent',
                            )}
                            onClick={() => setFilterString('')}
                        >
                            <XIcon className="size-5" />
                        </button>
                    </HoverTip>
                ) : (
                    <div className="absolute right-2 inset-y-0 flex items-center text-zinc-400 select-none pointer-events-none">
                        <InlineCode className="text-xs tracking-wide">ctrl+k</InlineCode>
                    </div>
                )}
            </div>
            <DropdownMenu>
                <HoverTip label="Filter & Sortierung">
                    <DropdownMenuTrigger asChild>
                        <button
                            className={cn(
                                'pressable h-8 w-8 inline-flex shrink-0 cursor-pointer items-center justify-center rounded-tile',
                                'border border-edge bg-track text-faint outline-none',
                                isFiltering
                                    ? 'border-accent/45 bg-accent/10 text-accent hover:border-accent/70 hover:bg-accent/20 hover:text-accent'
                                    : 'hover:border-edge-strong hover:bg-tile hover:text-fg',
                            )}
                            aria-label="Filter & Sortierung"
                        >
                            <SlidersHorizontalIcon className="h-4" />
                        </button>
                    </DropdownMenuTrigger>
                </HoverTip>
                <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>Spieler</DropdownMenuLabel>
                    <DropdownMenuCheckboxItem
                        checked={teamOnly}
                        onCheckedChange={setTeamOnly}
                        className="cursor-pointer"
                        onSelect={(event) => event.preventDefault()}
                    >
                        <span className="flex min-w-full items-center gap-2">
                            <span className="truncate-flex grow">Teammitglieder</span>
                            <TeamStar />
                        </span>
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem
                        checked={creatorOnly}
                        onCheckedChange={setCreatorOnly}
                        className="cursor-pointer"
                        onSelect={(event) => event.preventDefault()}
                    >
                        <span className="flex min-w-full items-center gap-2">
                            <span className="truncate-flex grow">Nur Creator</span>
                            <TagColor color={creatorColor} />
                        </span>
                    </DropdownMenuCheckboxItem>

                    <DropdownMenuSeparator />

                    <DropdownMenuLabel>Spielzeit</DropdownMenuLabel>
                    <DropdownMenuRadioGroup
                        value={maxPlayHours === null ? 'all' : String(maxPlayHours)}
                        onValueChange={(value) => setMaxPlayHours(value === 'all' ? null : Number(value))}
                    >
                        <DropdownMenuRadioItem value="all" className="cursor-pointer">
                            Alle
                        </DropdownMenuRadioItem>
                        {PLAYTIME_PRESETS.map((hours) => (
                            <DropdownMenuRadioItem
                                key={hours}
                                value={String(hours)}
                                className="cursor-pointer"
                            >
                                Unter {hours} Std.
                            </DropdownMenuRadioItem>
                        ))}
                    </DropdownMenuRadioGroup>
                    <div className="flex items-center gap-2 px-2 py-1.5 text-sm">
                        <span>Unter</span>
                        <Input
                            type="number"
                            min={1}
                            className="h-7 w-20"
                            placeholder="X"
                            value={maxPlayHours ?? ''}
                            //the menu treats typing as its keyboard navigation otherwise
                            onKeyDown={(e) => e.stopPropagation()}
                            onChange={(e) => {
                                const hours = parseFloat(e.target.value);
                                setMaxPlayHours(Number.isFinite(hours) && hours > 0 ? hours : null);
                            }}
                        />
                        <span>Std.</span>
                    </div>

                    <DropdownMenuSeparator />

                    <DropdownMenuLabel>Sortieren nach</DropdownMenuLabel>
                    <DropdownMenuRadioGroup
                        value={sortOrder}
                        onValueChange={(value) => setSortOrder(value as PlayerlistSortOrder)}
                    >
                        <DropdownMenuRadioItem value="joinOrder" className="cursor-pointer">
                            Beitrittsreihenfolge
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="recentlyJoined" className="cursor-pointer">
                            Zuletzt beigetreten
                        </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>

                    <DropdownMenuSeparator />

                    <DropdownMenuLabel>Name anzeigen als</DropdownMenuLabel>
                    <DropdownMenuRadioGroup
                        value={nameMode}
                        onValueChange={(v) => setNameMode(v as PlayerlistNameMode)}
                    >
                        {nameModes.map((mode) => (
                            <DropdownMenuRadioItem
                                key={mode.value}
                                value={mode.value}
                                className="cursor-pointer"
                                onSelect={(event) => event.preventDefault()}
                            >
                                <span className="flex min-w-full items-center gap-2">
                                    <span className="truncate-flex grow">{mode.label}</span>
                                    <TagColor color={mode.color} />
                                </span>
                            </DropdownMenuRadioItem>
                        ))}
                    </DropdownMenuRadioGroup>

                    {isFiltering || filterString.length ? (<>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="cursor-pointer" onClick={resetAll}>
                            <FilterXIcon className="mr-2 h-4 w-4" />
                            Zurücksetzen
                        </DropdownMenuItem>
                    </>) : null}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
const PlayerlistFilterMemo = memo(PlayerlistFilter);


type PlayerlistPlayerProps = {
    virtualItem: VirtualItem,
    player: PlayerlistPlayerType,
    playTime?: number,
    showGameData: boolean,
    modalOpener: (netid: number) => void,
    nameMode: PlayerlistNameMode,
}
//NOTE: the styles have been added to global.css since this component is rendered A LOT
function PlayerlistPlayer({ virtualItem, player, playTime, showGameData, modalOpener, nameMode }: PlayerlistPlayerProps) {
    //the list leads with the name the current search mode matches on
    const shownName = nameMode === 'rp'
        ? player.coreName ?? player.displayName
        : player.displayName;
    const job = player.coreJob
        ? player.coreJobGrade ? `${player.coreJob} · ${player.coreJobGrade}` : player.coreJob
        : null;
    const title = playTime !== undefined
        ? `Spielzeit: ${msToShortDuration(playTime * 60_000)}`
        : undefined;

    return (
        <div
            className="player"
            style={{
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
            }}
            title={title}
            onClick={() => modalOpener(player.netid)}
        >
            <div className="pid-block">
                <span className="pid-badge">{player.netid}</span>
            </div>
            {player.isCreator ? (
                <span className="pteam-badge" style={{ color: creatorColor }}>Creator</span>
            ) : null}
            <div className="pinfo">
                <span className="pname">
                    {player.isAdmin ? <TeamStar className="mr-1" /> : null}
                    {shownName}
                </span>
                {showGameData ? (<>
                    <span className="pchar">{player.coreName ?? 'Kein Charakter'}</span>
                    <span className="pjobs">{job ?? 'Keine Jobs'}</span>
                </>) : null}
            </div>
        </div>
    )
}


export default function Playerlist() {
    const playerlist = useAtomValue(playerlistAtom);
    const serverMutex = useAtomValue(serverMutexAtom);
    const openPlayerModal = useOpenPlayerModal();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [filterString, setFilterString] = useState('');
    const [nameMode, setNameMode] = useAtom(playerlistNameModeAtom);
    const [teamOnly, setTeamOnly] = useState(false);
    const [creatorOnly, setCreatorOnly] = useState(false);
    const [maxPlayHours, setMaxPlayHours] = useState<number | null>(null);
    const [sortOrder, setSortOrder] = useState<PlayerlistSortOrder>('joinOrder');
    const [playtimeResp, setPlaytimeResp] = useState<{ mutex: string | null, playtimes: PlaytimeMap }>();

    //The playerlist snapshot carries a playtime, but it is missing for anyone who
    //joined after the panel connected - their db data lands a moment after the
    //join event. So we refetch rather than trust that first value forever.
    const playtimesApi = useBackendApi<PlayerlistPlaytimesResp>({
        method: 'GET',
        path: '/player/playtimes',
        abortOnUnmount: true,
    });
    const fetchPlaytimes = async () => {
        const resp = await playtimesApi({ error: () => { } });
        if (!resp || 'error' in resp) return;
        setPlaytimeResp(resp);
    };

    useEffect(() => {
        const interval = setInterval(fetchPlaytimes, PLAYTIME_REFRESH_INTERVAL);
        return () => clearInterval(interval);
    }, []);
    useEffect(() => {
        const timeout = setTimeout(fetchPlaytimes, PLAYTIME_CHANGE_DEBOUNCE);
        return () => clearTimeout(timeout);
    }, [playerlist]);

    //netids are recycled across restarts, so a stale answer must be dropped
    const freshPlaytimes = playtimeResp && playtimeResp.mutex === serverMutex
        ? playtimeResp.playtimes
        : EMPTY_PLAYTIMES;
    const playTimeOf = (player: PlayerlistPlayerType) => freshPlaytimes[player.netid] ?? player.playTime;

    //only grow the rows once there is actually something to put in them
    const showGameData = useMemo(() => {
        return playerlist.some((player) => player.coreName || player.coreJob);
    }, [playerlist]);
    const rowHeight = showGameData ? ROW_HEIGHT_GAME_DATA : ROW_HEIGHT;

    //TODO: temporary logic, use fuse.js or something like that
    const filteredPlayerlist = useMemo(() => {
        let scoped = playerlist;
        if (teamOnly) scoped = scoped.filter((player) => player.isAdmin);
        if (creatorOnly) scoped = scoped.filter((player) => player.isCreator);
        if (maxPlayHours !== null) {
            const maxMinutes = maxPlayHours * 60;
            //an unknown playtime counts as new - that is what this filter is for
            scoped = scoped.filter((player) => (playTimeOf(player) ?? 0) < maxMinutes);
        }

        const rawFilter = filterString.trim().toLocaleLowerCase();
        if (rawFilter.length) {
            const pureFilter = cleanPlayerName(filterString).pureName;
            if (pureFilter !== 'emptyname') {
                scoped = scoped.filter((player) => {
                    return player.pureName?.includes(pureFilter)
                        || player.corePureName?.includes(pureFilter)
                        || player.corePureJob?.includes(pureFilter)
                        || player.corePureGroup?.includes(pureFilter)
                        || player.netid.toString().includes(pureFilter);
                });
            }
        }

        //the list arrives in join order, so the newest are simply at the end
        return sortOrder === 'recentlyJoined' ? [...scoped].reverse() : scoped;
    }, [playerlist, filterString, teamOnly, creatorOnly, maxPlayHours, sortOrder, freshPlaytimes]);

    //NOTE: I tried many algorithms to calculate the minimum width of the ID column,
    // but the simplest one was the best one when considering performance.
    const injectedStyle = useMemo(() => {
        const maxId = playerlist.at(-1)?.netid ?? 0;
        const idCharLength = Math.floor(Math.log10(maxId)) + 1; //+1 because log10(1...9) < 1
        return `.tx-playerlist .player .pid-block { min-width: ${idCharLength + 1}ch; }`; //+1 due to badge padding
    }, [playerlist]);

    // The virtualizer
    const rowVirtualizer = useVirtualizer({
        isScrollingResetDelay: 0,
        count: filteredPlayerlist.length,
        getScrollElement: () => (scrollRef.current as HTMLDivElement)?.getElementsByTagName('div')[0],
        estimateSize: () => rowHeight,
        overscan: 15,
    });
    const virtualItems = rowVirtualizer.getVirtualItems();

    //the rows change height the moment the first character data arrives
    useEffect(() => {
        rowVirtualizer.measure();
    }, [rowHeight]);

    const modalOpener = (netid: number) => {
        if (!serverMutex) return;
        openPlayerModal({ mutex: serverMutex, netid });
    }

    const hasActiveFilter = !!filterString || teamOnly || creatorOnly || maxPlayHours !== null;

    return (
        <>
            <PlayerlistFilterMemo
                filterString={filterString}
                setFilterString={setFilterString}
                nameMode={nameMode}
                setNameMode={setNameMode}
                teamOnly={teamOnly}
                setTeamOnly={setTeamOnly}
                creatorOnly={creatorOnly}
                setCreatorOnly={setCreatorOnly}
                maxPlayHours={maxPlayHours}
                setMaxPlayHours={setMaxPlayHours}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
            />

            <div
                className={cn(
                    'text-center m-1 text-warning-inline tracking-wider italic text-xs',
                    filteredPlayerlist.length !== playerlist.length && virtualItems.length ? 'block' : 'hidden'
                )}
            >
                Zeige {filteredPlayerlist.length} von {playerlist.length} Spielern.
            </div>
            <div
                className={cn(
                    'text-center m-6 text-muted-foreground tracking-wider italic',
                    virtualItems.length ? 'hidden' : 'block'
                )}
            >
                {playerlist.length && hasActiveFilter ? (
                    <p>
                        Keine Spieler gefunden.
                        <span className="text-xs block opacity-75">Filter zurücksetzen, um alle zu sehen.</span>
                    </p>
                ) : (
                    <p>
                        Keine Spieler online.
                        <span className="text-xs block opacity-75">Lad ein paar Freunde ein.</span>
                    </p>
                )}
            </div>

            <style>{injectedStyle}</style>
            <ScrollArea className="h-full select-none" ref={scrollRef}>
                <div
                    className="tx-playerlist"
                    style={{
                        height: `${rowVirtualizer.getTotalSize()}px`,
                        width: '100%',
                        position: 'relative',
                    }}
                >
                    {virtualItems.map((virtualItem) => {
                        const player = filteredPlayerlist[virtualItem.index];
                        return (
                            <PlayerlistPlayer
                                key={virtualItem.key}
                                virtualItem={virtualItem}
                                player={player}
                                playTime={playTimeOf(player)}
                                showGameData={showGameData}
                                modalOpener={modalOpener}
                                nameMode={nameMode}
                            />
                        );
                    })}
                </div>
            </ScrollArea>
        </>
    );
}
